import React from "react";
import { Box, Typography, Avatar, Button, Stack, Chip } from "@mui/material";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import dayjs from "dayjs";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
  redHover: "#b20710",
};

interface Guest {
  name: string;
  avatar: string;
}

interface FeaturedProps {
  title: string;
  bannerImage: string;
  startTime: string;
  endTime: string;
  guests: Guest[];
  isLive: boolean;
  isReplay: boolean;
  onActionClick: () => void;
}

const Featured: React.FC<FeaturedProps> = ({
  title,
  bannerImage,
  startTime,
  endTime,
  guests,
  isLive,
  isReplay,
  onActionClick,
}) => {
  const formattedTime = `${dayjs(startTime).format("MMM D, h:mm A")} - ${dayjs(
    endTime
  ).format("h:mm A")}`;

  const statusLabel = isLive ? "LIVE" : isReplay ? "Replay" : "Upcoming";
  const actionLabel = isLive
    ? "Join Now"
    : isReplay
      ? "Watch Replay"
      : "Remind Me";

  return (
    <Box px={{ xs: 2, md: 4 }}>
      <Typography
        fontFamily="Lexend"
        fontWeight={700}
        fontSize={{ xs: 20, sm: 24, md: 26 }}
        mb={2}
        sx={{ color: COLORS.text }}
      >
        Featured Events
      </Typography>
      <Box
        sx={{
          width: "100%",
          borderRadius: 3,
          mb: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          backgroundColor: COLORS.panel,
          border: `1px solid ${COLORS.border}`,
          color: COLORS.text,
          boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
          transition: "box-shadow 0.3s ease",
          "&:hover": {
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          },
        }}
      >
        {/* Banner */}
        <Box
          sx={{
            position: "relative",
            width: { xs: "100%", md: 360 },
            height: { xs: 200, md: "auto" },
            flexShrink: 0,
            overflow: "hidden",
            cursor: "pointer",
            "&:hover img": {
              transform: "scale(1.05)",
            },
          }}
        >
          <Box
            component="img"
            src={bannerImage}
            alt={title}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.4s ease",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)",
            }}
          />
          <Chip
            label={statusLabel}
            icon={isLive ? <LiveTvIcon fontSize="small" /> : <AccessTimeIcon />}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              px: 1.5,
              height: 24,
              fontWeight: 700,
              borderRadius: "12px",
              backgroundColor: isLive ? COLORS.red : "rgba(255,255,255,0.15)",
              color: COLORS.text,
              ".MuiChip-icon": { ml: 0.25, color: COLORS.text },
              boxShadow: isLive
                ? "0 2px 8px rgba(229,9,20,0.4)"
                : "0 2px 6px rgba(0,0,0,0.3)",
            }}
          />
        </Box>

        {/* Info Section */}
        <Box
          px={{ xs: 2, md: 3 }}
          py={{ xs: 2, md: 3 }}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          minHeight={{ md: 200 }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                background: "linear-gradient(90deg, #FF512F, #DD2476)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 1,
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              🎙️ {title}
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: COLORS.muted }}
              fontWeight={600}
              mb={1}
            >
              {formattedTime}
            </Typography>

            <Stack spacing={0.5}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ color: COLORS.text }}
              >
                Guests
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {guests.map((guest, idx) => (
                  <Stack
                    key={idx}
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: "16px",
                      px: 1,
                      py: 0.25,
                      backgroundColor: "rgba(255,255,255,0.06)",
                      transition: "transform 0.2s ease",
                      cursor: "default",
                      "&:hover": {
                        transform: "scale(1.05)",
                        borderColor: COLORS.red,
                        boxShadow: `0 0 8px rgba(229,9,20,0.3)`,
                      },
                    }}
                  >
                    <Avatar src={guest.avatar} sx={{ width: 26, height: 26 }} />
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        fontSize: "0.8rem",
                        maxWidth: 110,
                        color: COLORS.text,
                      }}
                    >
                      {guest.name}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Box>

          <Box mt={2} textAlign="right">
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon sx={{ fontSize: 20 }} />}
              onClick={onActionClick}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.85rem",
                px: 3,
                py: 1,
                borderRadius: 3,
                backgroundColor: isLive ? COLORS.red : COLORS.red,
                color: COLORS.text,
                boxShadow: isLive
                  ? "0 6px 16px rgba(229,9,20,0.4)"
                  : "0 6px 12px rgba(229,9,20,0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: COLORS.redHover,
                  transform: "scale(1.05)",
                  boxShadow: "0 8px 20px rgba(229,9,20,0.5)",
                },
              }}
            >
              {actionLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Featured;
